import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import localSettings from "@app/config/LocalSettings";
import { Actions as AppActions, initializeApp, reinitializeApp } from "@app/store/app/actions";
import { fetchSchemaForEntityType, Actions as SchemaActions } from "@app/store/schema/actions";
import personFieldModel from "@app/util/fieldModel/PersonFieldModel";

import { Actions, applyListViewSettings, fetchList, setOfferedFilters } from "./actions";

export interface PeopleListViewState extends ViewState {
  error: undefined | unknown;
  loading: boolean;
  offerFilters?: FilterModel;
  totalFilteredRecords: number;
  totalRecords: number;
}

const initialState: PeopleListViewState = {
  ...personFieldModel.getDefaultListViewState(),
  error: undefined,
  // loading is true by default to avoid flashing a "no rows" overlay when the page
  // is just opened but loading is not yet started
  loading: true,
  totalFilteredRecords: 0,
  totalRecords: 0,
};

const people = createReducer<PeopleListViewState, Actions | AppActions | SchemaActions>(
  initialState
)
  // TODO: change this to catch fetchCustomFields.success for people entity type
  // to update view state every time custom fields are modified
  // load view settings after app initialization to ensure custom fields are fetched
  // OR not change, but add one more action
  .handleAction([initializeApp.success, reinitializeApp.success], (state) => ({
    ...state,
    ...localSettings.getViewSettings("people/listView", personFieldModel),
  }))
  .handleAction(applyListViewSettings, (state, { payload }) => ({
    ...state,
    columns: payload.columns ?? state.columns,
    filter: payload.filter ?? state.filter,
    range: payload.range ?? state.range,
    // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
    selectedSavedFilterId:
      "selectedSavedFilterId" in payload
        ? payload.selectedSavedFilterId
        : state.selectedSavedFilterId,
    sort: payload.sort ?? state.sort,
  }))
  .handleAction(fetchList.request, (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleAction(fetchList.success, (state, action) => ({
    ...state,
    loading: false,
    totalFilteredRecords: action.payload.totalFilteredRecords,
    totalRecords: action.payload.totalRecords,
  }))
  .handleAction(fetchList.failure, (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleAction(setOfferedFilters, (state, { payload }) => ({
    ...state,
    offerFilters: payload,
  }))
  .handleAction(fetchSchemaForEntityType.success, (state, { payload }) => {
    const viewState = localSettings.getViewSettings("people/listView", personFieldModel);
    if (payload.entityType === EntityType.PERSON) {
      viewState.columns = viewState.columns.filter(({ field: column }) => {
        const field = personFieldModel.getByName(column.name);
        return field && (field.isReadable || field.isSystemRequired) && !field.isArchived;
      });
    }
    return {
      ...state,
      ...viewState,
    };
  });

export type PeopleActions = Actions;
export default people;
